import { ExternalLink, X } from 'lucide-react';
import type { UnifiedInvoice } from '../../../../lib/userInvoicesApi';

interface InvoicePreviewModalProps {
  invoice: UnifiedInvoice;
  onClose: () => void;
}

const SOURCE_LABELS: Record<string, string> = {
  smartbill: 'SmartBill',
  manual: 'Încărcare manuală',
  email: 'Import email',
};

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString('ro-RO') : '—';
}

function formatMoney(amount: number, currency: string): string {
  return `${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

/**
 * Invoice detail + PDF preview (CLE-238). Shows the unified invoice's fields and
 * embeds the PDF when a short-lived download URL is available. SmartBill rows
 * only expose a PDF once it has been archived on demand, so we degrade
 * gracefully to a "no preview" state with a download link when present.
 */
export function InvoicePreviewModal({ invoice, onClose }: InvoicePreviewModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Factura ${invoice.invoiceNumber}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Factura {invoice.invoiceNumber}</h2>
            <p className="text-[11px] uppercase tracking-wide text-gray-400">
              {SOURCE_LABELS[invoice.source] ?? invoice.source}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Închide"
          >
            <X size={18} />
          </button>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 border-b border-gray-200 px-5 py-4 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Tip</dt>
            <dd className="text-gray-900">
              {invoice.flow === 'income' ? 'Venituri' : 'Cheltuieli'}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Status</dt>
            <dd className="text-gray-900">{invoice.status}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Categorie</dt>
            <dd className="text-gray-900">{invoice.category ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Descriere</dt>
            <dd className="text-gray-900">{invoice.description ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Data emiterii</dt>
            <dd className="text-gray-900">{formatDate(invoice.issueDate)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Termen limită</dt>
            <dd className="text-gray-900">{formatDate(invoice.dueDate)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Net</dt>
            <dd className="text-gray-900">{formatMoney(invoice.net, invoice.currency)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">TVA</dt>
            <dd className="text-gray-900">{formatMoney(invoice.vat, invoice.currency)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Total</dt>
            <dd className="font-semibold text-gray-900">
              {formatMoney(invoice.total, invoice.currency)}
            </dd>
          </div>
        </dl>

        <div className="min-h-[320px] flex-1 overflow-auto bg-gray-50 p-4">
          {invoice.pdfUrl ? (
            <iframe
              title={`Previzualizare PDF pentru factura ${invoice.invoiceNumber}`}
              src={invoice.pdfUrl}
              className="h-[60vh] w-full rounded border border-gray-200 bg-white"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 py-12 text-center text-sm text-gray-500">
              <p>Nicio previzualizare PDF disponibilă pentru această factură.</p>
              {invoice.source === 'smartbill' && (
                <p className="text-xs text-gray-400">
                  PDF-urile SmartBill sunt arhivate la cerere și pot să nu fie încă în cache.
                </p>
              )}
            </div>
          )}
        </div>

        {invoice.pdfUrl && (
          <div className="flex justify-end border-t border-gray-200 px-5 py-3">
            <a
              href={invoice.pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-brand-600 hover:underline"
            >
              Deschide PDF în filă nouă
              <ExternalLink size={14} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
